import { DetailsList, DetailsListLayoutMode, Dialog, DirectionalHint, Dropdown, HoverCard, HoverCardType, IColumn, IconButton, IGroup, ITag, Link, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator, SelectionMode, Spinner, Stack, TagPicker, TooltipHost } from '@fluentui/react';
import moment from 'moment';
import * as React from 'react';
import { renameBlobFile, updateCourseWorkTags, updateHiddenCoursework, updateHiddenCourseworkForAccreditor } from '../data/coursework';
import { getFileName, getStudentName, humanFileSize, isCanvasStudent, isCanvasTeacher, isCurrentUserAStudentOrTeacherInCourse, onOpenFileInTab, saveBase64, sendApiRequest, thisIsDevEnvironment } from '../lib/helper';
import { useAssignmentContext } from '../providers/assignmentsprovider';
import { ICourseWorkFlat } from '../types/IAssignment';
import { ICourseWorkTagMapping } from '../types/IAssignmentTags';
import { IListViewCourseworksProperties, noAssignmentsComponentString } from './coursework';
import ImageSize from './imageSize';
import PreviewCard from './previewCard';
import CourseTagsDisplay from './shared/courseTagsDisplay';
import SlideShow from './slideShow.tsx';

const CourseWorkListView: React.FunctionComponent<{ props: IListViewCourseworksProperties }> = ({ props }) => {

    let files = props.files;
    if (props.authContext && !props.authContext?.KeplerAuthStatus?.isKeplerAdministrator && props.accreditorHiddenCourseWorks && props.accreditorHiddenCourseWorks.length > 0) {
        files = files.filter(f => !props.accreditorHiddenCourseWorks?.includes(f.FilePath));
    }
    const assignmentContext = useAssignmentContext();
    const currentUserIsTeacherInCourse = props.authContext && isCanvasTeacher(props.authContext) && isCurrentUserAStudentOrTeacherInCourse(props.authContext, props.assignment.Course);
    const currentUserIsStudentInCourse = props.authContext && isCanvasStudent(props.authContext) && isCurrentUserAStudentOrTeacherInCourse(props.authContext, props.assignment.Course);
    const isCurrentUserAllowedToSeeHiddenFilesForCourse = props.authContext && (props.authContext?.KeplerAuthStatus?.isKeplerAdministrator || currentUserIsTeacherInCourse || currentUserIsStudentInCourse);
    if (!props.showHiddenCourseWorks && isCurrentUserAllowedToSeeHiddenFilesForCourse) {
        files = files.filter(f => !f.IsCourseWorkHidden);
    }
    else if (isCurrentUserAllowedToSeeHiddenFilesForCourse && props.showHiddenCourseWorks) {
        files = files.filter(f => f.IsCourseWorkHidden);
    }

    const [accreditorHiddenCourseWorks, setAccreditorHiddenCourseWorks] = React.useState<string[]>(props.accreditorHiddenCourseWorks || []);
    const [error, setError] = React.useState<string | undefined>();
    const [assignmentDialog, setAssignmentDialog] = React.useState<ICourseWorkFlat>();
    const [filesState, setFilesState] = React.useState<ICourseWorkFlat[]>(files);
    const [selectedReassignment, setSelectedReassignment] = React.useState<string>();
    const [isReassigning, setIsReassigning] = React.useState(false);
    const [slideShowItem, setSlideShowItem] = React.useState<ICourseWorkFlat>();
    const [isHidingCourseWork, setIsHidingCourseWork] = React.useState<string | undefined>(undefined);

    const _onRenderPreviewHoverCard = (item: ICourseWorkFlat, index: number | undefined): JSX.Element => {
        return <div style={{ padding: 10, width: 360, height: 426}}>
            <PreviewCard
                index={index || 0}
                file={item}                
                rootStyle={ { width: "inherit", height: "inherit", maxWidth: "inherit" } }
                semester={props.semseter}
                shouldGetDataFromCanvasAPIs={props.shouldGetDataFromCanvasAPIs}
                setSlideShowItem={(file) => {
                    setSlideShowItem(file);
                }}
                maxFileSizeBeforeStream={props.maxFileSizeBeforeStream}
            />
        </div>
    }


    const currentUseIsTeacherInCourseAndCourseIsActive = currentUserIsTeacherInCourse && props.assignment.Course.IsCurrentlyActive;
    const _getColumns = (): IColumn[] => {
        const hasPartColumn = props.assignmentTagMapping && props.assignmentTagMapping.TagType !== "none" && props.allowedTags && props.allowedTags.length > 0;
        let retVal: IColumn[] = [
            {
                key: "name",
                name: "Name",
                minWidth: 500,
                isResizable: true,
                fieldName: "FileName",
                onRender: (item: ICourseWorkFlat, index: number | undefined) => {
                    const fileName = getFileName(props.authContext, props.accreditationRulesContext?.Result, item, index || 0);
                    const apiPath = item?.FilePath && `/api/blobStorage/getFileStream/${item.FileName}?path=${encodeURIComponent(item.FilePath)}`;
                    const url = thisIsDevEnvironment ? `https://localhost:8080${apiPath}` : apiPath;
                    return <Stack horizontal tokens={{ childrenGap: 10 }}>
                        <HoverCard
                            cardOpenDelay={400}
                            type={HoverCardType.plain}
                            plainCardProps={{
                                onRenderPlainCard: () => _onRenderPreviewHoverCard(item, index),
                                directionalHint: DirectionalHint.rightCenter,
                                calloutProps: {
                                    isBeakVisible: true,
                                },
                            }}>
                            <Link title={fileName} href={url} onClick={(ev) => onOpenFileInTab(ev, url, fileName)}>{fileName}</Link>
                        </HoverCard>
                        {currentUseIsTeacherInCourseAndCourseIsActive && item.IsMyCourseWork && props.studentsInCourse.length > 0 && <Link onClick={() => setAssignmentDialog(item)}>(Re-assign)</Link>}
                    </Stack>
                }
            },
            {
                key: "pictureSize",
                name: "Picture Size",
                isResizable: true,
                minWidth: 200,
                fieldName: "PictureSize",
                onRender: (item: ICourseWorkFlat) => {
                    return <ImageSize file={item} />
                }
            },
            {
                key: "fileSize",
                name: "File Size",
                isResizable: true,
                minWidth: 100,
                fieldName: "FileSize",
                onRender: (item: ICourseWorkFlat) => {
                    return humanFileSize(item.FileSize)
                }
            },
            {
                key: "modified",
                name: "Uploaded Date",
                isResizable: true,
                minWidth: 110,
                fieldName: "Modified",
                onRender: (item: ICourseWorkFlat) => {
                    return props.shouldGetDataFromCanvasAPIs ? moment(item.BlobProperties.lastModified).format("M/D/YYYY h:mm A") : `${props.semseter.Semester} ${props.semseter.Year}`
                }
            }
        ];

        if (props.authContext && (props.authContext?.KeplerAuthStatus?.isKeplerAdministrator || currentUserIsTeacherInCourse || currentUserIsStudentInCourse)) {
            retVal.push({
                key: "courseWorkVisibility",
                name: "File Visbility",
                isResizable: true,
                minWidth: 200,
                fieldName: "FileVisibility",
                onRender: (item: ICourseWorkFlat) => {
                    if (item.IsMyCourseWork || currentUserIsTeacherInCourse || props.authContext?.KeplerAuthStatus?.isKeplerAdministrator) {
                        const isHidden = item.IsCourseWorkHidden;
                        return <TooltipHost content={isHidingCourseWork === item.FilePath ? "Hiding file..." : `The file is ${isHidden ? "hidden" : "visible"} to other users. Click here to toggle the visibility. Use the "Show Hidden Files" toggle to show or hide hidden files.`}>
                            {isHidingCourseWork !== item.FilePath ? <IconButton
                                disabled={isHidingCourseWork !== undefined}
                                onClick={() => {
                                    setIsHidingCourseWork(item.FilePath);
                                    updateHiddenCoursework(item.FilePath, !isHidden).then(() => {
                                        let existingState = [...filesState];
                                        existingState.find(f => f.FilePath === item.FilePath)!.IsCourseWorkHidden = !isHidden;
                                        setFilesState(existingState);
                                        const assignment = assignmentContext.SelectedAssignment;
                                        if (assignment) {
                                            assignmentContext.SetSelectedAssignment(undefined)
                                            assignmentContext.SetSelectedAssignment(assignment)
                                        }
                                    }).catch(err => {
                                        console.error("There was an error showing or hiding the file.", err);
                                        setError("There was an error showing or hiding the file. See console for more information.");
                                    }).finally(() => {
                                        setIsHidingCourseWork(undefined);
                                    })
                                }} iconProps={{ iconName: isHidden ? "hide" : "RedEye" }} /> : <Spinner style={{ textAlign: "left", alignContent: "start"}} />}
                                </TooltipHost>
                    }
                }
            })
        }

        if (props.authContext && props.authContext?.KeplerAuthStatus?.isKeplerAdministrator) {
            retVal.push({
                key: "accreditorVisibility",
                name: "Accreditor Visbility",
                isResizable: true,
                minWidth: 200,
                fieldName: "AccreditorVisibility",
                onRender: (item: ICourseWorkFlat) => {
                    const isHidden = accreditorHiddenCourseWorks?.includes(item.FilePath);
                    return <IconButton onClick={() => {
                        updateHiddenCourseworkForAccreditor(item.FilePath, !isHidden).then(() => {
                            if (!isHidden) {
                                accreditorHiddenCourseWorks?.push(item.FilePath);
                                setAccreditorHiddenCourseWorks([...accreditorHiddenCourseWorks]);
                            }
                            else {
                                const accreditorHiddenCourseWorksFiltered = accreditorHiddenCourseWorks.filter(c => c.toLowerCase() !== item.FilePath.toLowerCase())
                                setAccreditorHiddenCourseWorks(accreditorHiddenCourseWorksFiltered);
                            }
                        })
                    }} iconProps={{ iconName: isHidden ? "hide" : "RedEye" }} />
                }
            })
        }
        if (hasPartColumn) {
            retVal.splice(1, 0, {
                key: "part",
                name: "Part",
                minWidth: 200,
                isResizable: true,
                fieldName: "Part",
                onRender: (item: ICourseWorkFlat) => {
                    return <Stack horizontal>
                        {item.IsMyCourseWork ? <TagPicker
                            disabled={!item.IsMyCourseWork}
                            selectedItems={item?.Tags?.filter(t => t && t.length > 0).map(t => ({ key: t, name: t }))}
                            itemLimit={props.assignmentTagMapping!.TagType === "single" ? 1 : undefined}
                            onResolveSuggestions={(filter: string, selectedItems?: ITag[]): ITag[] | PromiseLike<ITag[]> => {
                                let tagsToShow: string[] = [];
                                const filteredTags = props.allowedTags!.filter(t => !selectedItems?.map(t => t.key).includes(t.Tag) && t.Tag.toLowerCase().startsWith(filter.toLowerCase()));
                                if (filteredTags.length > 0) {
                                    tagsToShow = filteredTags.map(t => t.Tag);
                                }
                                return tagsToShow.map(t => ({ key: t, name: t }));
                            }}
                            onEmptyResolveSuggestions={(selectedItems) => props.allowedTags!.filter(t => !selectedItems?.map(t => t.key).includes(t.Tag)).map(t => ({ name: t.Tag, key: t.Tag })) || []}
                            onChange={(selectedItems) => {
                                const originalTags = [...item.Tags || []];
                                const newTags = selectedItems?.map(t => t.key?.toString()) || [];
                                const tagMapping: ICourseWorkTagMapping = {
                                    CourseWorkPath: item.FilePath,
                                    Tags: newTags
                                }
                                item.Tags = newTags;
                                updateCourseWorkTags(tagMapping).catch(err => {
                                    console.log("Error");
                                    setError("Unable to set tag");
                                    item.Tags = originalTags;
                                });

                            }} /> : <CourseTagsDisplay tags={item.Tags || []} />}
                    </Stack>
                }
            });
        }
        return retVal;
    };

    const _getGroups = (items: ICourseWorkFlat[]): IGroup[] => {
        let groups: any = {};
        items.sort((a, b) => (a.Student < b.Student ? -1 : a.Student > b.Student ? 1 : 0))
            .forEach((item, index) => {
                let group: number = groups[item.Student] || 0;
                group = group + 1;
                groups[item.Student] = group;
            });
        let index = 0;
        return Object.keys(groups).map((g, i) => {
            const retVal: IGroup = {
                key: `group-${i}-${g}`,
                name: getStudentName(props.authContext, props.accreditationRulesContext?.Result, g, i),
                startIndex: index,
                count: groups[g],
                isCollapsed: false
            };
            index = retVal.startIndex + retVal.count;
            return retVal;
        });
    };

    const items = ((props.selectedMyCoursework && props.currentStudent) ? [...filesState].filter(c => c.Student === props.currentStudent) : [...filesState]).filter(f => f.MatchesCurrentSearch)
    return <Stack>
        {error && <MessageBar messageBarType={MessageBarType.error}>{error}</MessageBar>}
        <DetailsList
            layoutMode={DetailsListLayoutMode.fixedColumns}
            groups={_getGroups(items)}
            items={items}
            selectionMode={SelectionMode.none}
            columns={_getColumns()}
        />
        {assignmentDialog && <Dialog
            hidden={false}
            modalProps={{ isBlocking: true }}
            title={`Assign File: ${assignmentDialog.FileName}`}
            onDismiss={() => setAssignmentDialog(undefined)}

        >
            <Stack tokens={{ childrenGap: 10 }}>
                <Dropdown selectedKey={selectedReassignment || assignmentDialog.Student} options={props.studentsInCourse.map(s => ({ key: s, text: s }))} onChange={(_ev, option) => {
                    setSelectedReassignment(option?.key!.toString())
                }} />
                {isReassigning && <ProgressIndicator />}
                <Stack horizontal tokens={{ childrenGap: 10 }}>
                    <PrimaryButton disabled={isReassigning} onClick={() => {
                        if (selectedReassignment) {
                            setIsReassigning(true);
                            const newPath = assignmentDialog.FilePath.replace(assignmentDialog.Student, selectedReassignment);
                            renameBlobFile(assignmentDialog.FilePath, newPath).then(() => {
                                let existingState = [...filesState];
                                existingState.find(f => f.FilePath === assignmentDialog.FilePath)!.Student = selectedReassignment;
                                existingState.find(f => f.FilePath === assignmentDialog.FilePath)!.IsMyCourseWork = false;
                                setFilesState(existingState);
                                setAssignmentDialog(undefined);
                            }).catch(err => {
                                console.error(err);
                                alert("There was an error saving the file. See the console for more information.");
                            }).finally(() => {
                                setIsReassigning(false);
                            })
                        }
                        else {
                            alert("You must select a student to assign the file to");
                        }
                    }}>Save</PrimaryButton>
                </Stack>
            </Stack>
        </Dialog>}
        {slideShowItem && <SlideShow {...{
            maxFileSizeBeforeStream: props.maxFileSizeBeforeStream,
            currentFile: slideShowItem, files: items, onClose: () => {
                setSlideShowItem(undefined);
            }, authContext: props.authContext, accreditationRulesContext: props.accreditationRulesContext
        }} />}
        {items.length === 0 && <Stack horizontalAlign="start">{noAssignmentsComponentString(props.studentsAssignedToAccreditor, props.showHiddenCourseWorks)}</Stack>}
    </Stack>;
};

export default CourseWorkListView;